import { toHexFromId } from '#~/api/index.js';
import type {
  BridgeSetupCapabilities,
  EnsureInitialRoomInventoryOrganization,
  EnsureInitialRoomInventoryRoom,
  InspectInitialRoomInventoryResult
} from '#~/api/agent/registry.js';
import type { Organization } from '@studio/models/organization';
import type { Room } from '@studio/models/room';
import type {
  AgentRegistryGetOrganizationResponse,
  AgentRegistryGetRoomResponse
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { AutoLinkState } from '../verify-auto-link/v2/auto-link-types.js';

type InventoryChangedState = Extract<AutoLinkState, { readonly status: 'inventory_changed' }>;
type SuccessState = Extract<AutoLinkState, { readonly status: 'success' }>;

export type PrimaryInventoryInspection =
  | { readonly status: 'available'; readonly inventory: InspectInitialRoomInventoryResult }
  | { readonly status: 'unavailable'; readonly cause: unknown };

/**
 * Adapt the ensure RPC's minimal primary snapshot to Root's existing inventory
 * decision model. Per think-in-systems + reference-rules-in-comments
 * (ai.knowledge/global), one adapter keeps replica and primary paths aligned.
 */
export const toAuthoritativeBootstrapRooms = (
  rooms: readonly EnsureInitialRoomInventoryRoom[]
): (AgentRegistryGetRoomResponse & { initialBillingPending?: boolean | undefined })[] =>
  rooms.map((room) => ({
    $type: 'api.v1.AgentRegistryGetRoomResponse',
    error: undefined,
    id: room.id,
    createdAt: 0,
    name: room.name,
    organizationId: room.organizationId,
    initialBillingPending: room.initialBillingPending,
    agents: [],
    permissions: undefined,
    bootstrapInventoryOnly: true
  }));

/** Keep hydrated replica records while enforcing primary cardinality/order. */
export const mergeAuthoritativeBootstrapRooms = (
  replicaRooms: readonly AgentRegistryGetRoomResponse[],
  authoritativeRooms: readonly EnsureInitialRoomInventoryRoom[]
): (AgentRegistryGetRoomResponse & { initialBillingPending?: boolean | undefined })[] => {
  const replicaById = new Map<Room['id'], AgentRegistryGetRoomResponse>();
  for (const room of replicaRooms) {
    const id = toHexFromId<Room['id']>(room.id);
    if (id !== undefined) replicaById.set(id, room);
  }
  return authoritativeRooms.map((room) => {
    const id = toHexFromId<Room['id']>(room.id);
    const hydrated =
      (id === undefined ? undefined : replicaById.get(id)) ??
      toAuthoritativeBootstrapRooms([room])[0]!;
    return room.initialBillingPending === undefined
      ? hydrated
      : { ...hydrated, initialBillingPending: room.initialBillingPending };
  });
};

/** Preserve the replica's hydrated bridge and permission metadata for picker use. */
export const toReplicaBootstrapInventoryRooms = (
  rooms: readonly AgentRegistryGetRoomResponse[]
): EnsureInitialRoomInventoryRoom[] =>
  rooms.map((room) => ({
    id: room.id,
    name: room.name,
    organizationId: room.organizationId,
    activeBridges: room.agents.map((agent) => ({ id: agent.id, name: agent.name })),
    // Linking is a mutation, so missing replica permissions remain fail-closed.
    // Per rule: absent-data-gates-permissively (ai.knowledge/global).
    canLinkBridge: room.permissions?.canUpdate ?? false
  }));

/**
 * Prefer the primary snapshot, but keep already-provisioned accounts bootable
 * during a staggered backend/frontend rollout. Replica fallback is deliberately
 * forbidden at zero Studios because only the primary can authorize zero-to-one
 * creation without recreating the cross-machine duplicate race.
 * Per stay-in-scope + reference-rules-in-comments (ai.knowledge/global).
 */
export const resolveInitialBootstrapInventory = (
  replicaRooms: readonly AgentRegistryGetRoomResponse[],
  inspection: PrimaryInventoryInspection
): {
  readonly rooms: AgentRegistryGetRoomResponse[];
  readonly pickerRooms: readonly EnsureInitialRoomInventoryRoom[];
  readonly ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[];
  /** Only the primary advertises capabilities; a replica fallback has none. */
  readonly capabilities?: BridgeSetupCapabilities | undefined;
  readonly source: 'primary' | 'replica_fallback';
} => {
  if (inspection.status === 'available') {
    return {
      rooms: mergeAuthoritativeBootstrapRooms(replicaRooms, inspection.inventory.rooms),
      pickerRooms: inspection.inventory.rooms,
      ownedOrganizations: inspection.inventory.ownedOrganizations,
      capabilities: inspection.inventory.capabilities,
      source: 'primary'
    };
  }

  if (replicaRooms.length === 0) {
    throw inspection.cause;
  }

  return {
    rooms: [...replicaRooms],
    pickerRooms: toReplicaBootstrapInventoryRooms(replicaRooms),
    ownedOrganizations: [],
    source: 'replica_fallback'
  };
};

/** Prefer the primary ensure snapshot; use replica only for pre-ensure exits. */
export const resolveInventoryChangedBootstrapInventory = async (
  state: InventoryChangedState,
  fetchReplicaRooms: () => Promise<AgentRegistryGetRoomResponse[]>
): Promise<{
  readonly rooms: AgentRegistryGetRoomResponse[];
  readonly ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[];
}> => {
  if (state.rooms !== undefined) {
    return {
      rooms: toAuthoritativeBootstrapRooms(state.rooms),
      ownedOrganizations: state.ownedOrganizations ?? []
    };
  }
  return { rooms: await fetchReplicaRooms(), ownedOrganizations: [] };
};

/** Use the created room returned by the primary before consulting replicas. */
export const resolveSuccessfulBootstrapInventory = async (
  state: SuccessState,
  fetchReplicaRooms: () => Promise<AgentRegistryGetRoomResponse[]>,
  fetchRoomById: (roomId: Room['id']) => Promise<AgentRegistryGetRoomResponse | undefined>
): Promise<{
  readonly rooms: AgentRegistryGetRoomResponse[];
  readonly ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[];
}> => {
  if (state.room !== undefined) {
    return {
      rooms: toAuthoritativeBootstrapRooms([state.room]),
      ownedOrganizations: state.ownedOrganizations ?? []
    };
  }

  const rooms = await fetchReplicaRooms();
  if (rooms.some((room) => toHexFromId<Room['id']>(room.id) === state.roomId)) {
    return { rooms, ownedOrganizations: [] };
  }
  const linkedRoom = await fetchRoomById(state.roomId);
  return {
    rooms: linkedRoom === undefined ? rooms : [...rooms, linkedRoom],
    ownedOrganizations: []
  };
};

/** Merge owner metadata from the primary snapshot over a possibly stale list. */
export const mergeAuthoritativeBootstrapOrganizations = (
  replicaOrganizations: readonly AgentRegistryGetOrganizationResponse[],
  ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[]
): AgentRegistryGetOrganizationResponse[] => {
  const merged = new Map<Organization['id'], AgentRegistryGetOrganizationResponse>();
  for (const organization of replicaOrganizations) {
    const id = toHexFromId<Organization['id']>(organization.id);
    if (id !== undefined) merged.set(id, organization);
  }
  for (const organization of ownedOrganizations) {
    const id = toHexFromId<Organization['id']>(organization.id);
    if (id === undefined) continue;
    const existing = merged.get(id);
    if (existing !== undefined) merged.set(id, { ...existing, name: organization.name });
  }
  return [...merged.values()];
};

/** The verified zero-to-one winner must pass through the linked-room boot gate. */
export const shouldUseAuthoritativeLinkedRoomBoot = (
  roomId: Room['id'],
  linkedAgentId: string | undefined,
  hydratedAgentCount: number,
  verifiedInitialRoomId: Room['id'] | undefined
): boolean =>
  linkedAgentId !== undefined && (hydratedAgentCount > 0 || verifiedInitialRoomId === roomId);
